/**
 * Deterministic, dependency-free SVG cover placeholder generator.
 *
 * Returns a `data:image/svg+xml` URL that renders a styled book-cover-ish
 * panel with the title + author initials. Used as the final fallback when
 * a real cover URL cannot be loaded.
 *
 * Pure function — same inputs always produce the same SVG, so the browser
 * can cache it and React keeps stable image identity.
 */

/**
 * Detect a "no cover available" placeholder image that still loads with HTTP 200.
 *
 * Google Books serves its "image not available" graphic from /books/content at
 * two fixed pixel sizes — 128x170 (thumbnail) and 575x750 (full) — and that same
 * 575x750 graphic is sometimes copied into our Supabase Storage `book-covers`
 * bucket. None are real covers. We match those *exact* dimensions (±2px), scoped
 * to those two sources, so a genuine cover (whose size won't coincide with these
 * specific values) is never rejected.
 *
 * Aspect ratio is deliberately NOT used: the 128x170 and 575x750 placeholders
 * have different ratios (1.328 vs 1.304), so a ratio band misses one of them.
 *
 * Matches let the cover recovery chain (Open Library / Archive.org / generated
 * placeholder) run instead of displaying the placeholder.
 */
// Google Books' "image not available" graphic, at every exact pixel size it has
// been observed at — including copies cached verbatim into our Storage
// book-covers bucket. All have plausible portrait ratios (128x170≈1.33,
// 575x750≈1.30, 300x391≈1.30), so the aspect heuristic below cannot catch them;
// they must be matched by exact dimensions.
// Exported for coverRulesInSync.test.ts: the server's cover gate
// (supabase/functions/_shared/bookCover.ts) must reject exactly these too.
export const NO_COVER_DIMENSIONS: ReadonlyArray<readonly [number, number]> = [
  [128, 170],
  [575, 750],
  [300, 391],
];

export function isNoCoverPlaceholder(img: HTMLImageElement): boolean {
  const src = img.currentSrc || img.src || "";
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return false;

  const matches = (pw: number, ph: number) =>
    Math.abs(w - pw) <= 2 && Math.abs(h - ph) <= 2;

  const isStorageCover = /\/storage\/v1\/object\/public\/book-covers\//.test(src);
  const isCoverSource =
    /books\.google(?:usercontent)?\.com/.test(src) ||
    /covers\.openlibrary\.org/.test(src) ||
    /archive\.org\/(?:services\/img|download)/.test(src) ||
    isStorageCover;

  // The "image not available" graphic at any of its exact sizes — from Google
  // Books OR copied into our Storage book-covers bucket. (Previously only the
  // Google 128x170/575x750 and Storage 575x750 were caught, so a Storage 128x170
  // and the 300x391 variant slipped through and never triggered recovery.)
  if (isCoverSource && NO_COVER_DIMENSIONS.some(([pw, ph]) => matches(pw, ph))) return true;

  // Implausible-for-a-book-cover dimensions from a KNOWN cover source: a broken
  // thumbnail (e.g. 12x15), a "no cover" mini-graphic (60x40), or a wrong-item
  // landscape/oversized scan (180x120, 320x220, 885x1920). Real covers observed
  // in the catalogue are w≥128, h≥166, ratio 1.29–1.69, so these bounds keep a
  // safe margin. Scoped to cover hosts so non-cover images (avatars, icons) are
  // never affected.
  if (isCoverSource) {
    const ratio = h / w;
    if (w < 120 || h < 150 || ratio < 1.2 || ratio > 1.75) return true;
  }
  return false;
}

/**
 * Shared onLoad/onError handler for raw cover <img>s that do NOT go through
 * EnhancedBookCover (e.g. FilmBookCover, publisher grids, search results,
 * community cards). Swaps the image to a generated SVG placeholder when it fails
 * to load OR loads as a known "no cover" placeholder / implausibly-sized cover.
 *
 * Title is read from the img's `alt`; optional author from `data-cover-author`.
 * Typed structurally so this stays dependency-free and still binds to React's
 * onLoad/onError. Real covers are untouched (isNoCoverPlaceholder is conservative
 * and cover-host scoped).
 */
export function handleCoverImg(e: {
  currentTarget: HTMLImageElement;
  type: string;
}): void {
  const img = e.currentTarget;
  const failed = e.type === "error" || isNoCoverPlaceholder(img);
  if (!failed) return;
  const title = img.getAttribute("data-cover-title") || img.alt || "Untitled";
  const author = img.getAttribute("data-cover-author") || undefined;
  const placeholder = generateCoverPlaceholder(title, author);
  if (img.src !== placeholder) {
    img.removeAttribute("srcset");
    img.src = placeholder;
    img.style.display = "";
  }
}

// Curated palette tuned to match the dark slate aesthetic.
const PALETTES: Array<[string, string]> = [
  ["#1e293b", "#334155"], // slate
  ["#1e1b4b", "#312e81"], // indigo
  ["#0f172a", "#1e3a8a"], // navy
  ["#3b0764", "#581c87"], // purple
  ["#0c4a6e", "#155e75"], // teal/cyan
  ["#3f1d38", "#7c2d52"], // wine
  ["#422006", "#713f12"], // amber-brown
];

function hash(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h;
}

function initials(title: string, author?: string | null): string {
  const src = (author && author.trim()) || title || "?";
  const parts = src
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "");
  return parts.join("") || "?";
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function wrap(text: string, maxCharsPerLine: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = w;
      if (lines.length === maxLines - 1) break;
    } else {
      current = candidate;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // Truncate last line if overflowing
  if (lines.length === maxLines && current.length > maxCharsPerLine) {
    lines[maxLines - 1] = current.slice(0, maxCharsPerLine - 1) + "…";
  }
  return lines;
}

export interface CoverPlaceholderOptions {
  width?: number;
  height?: number;
}

/**
 * Build an SVG data-URI for a placeholder cover.
 * Aspect defaults to 2:3 (book poster).
 */
export function generateCoverPlaceholder(
  title: string,
  author?: string | null,
  opts: CoverPlaceholderOptions = {}
): string {
  const w = opts.width ?? 400;
  const h = opts.height ?? 600;
  const safeTitle = (title || "Untitled").trim();
  const safeAuthor = (author || "").trim();

  const seed = hash(`${safeTitle}|${safeAuthor}`);
  const [c1, c2] = PALETTES[seed % PALETTES.length];
  const accent = `hsl(${seed % 360}, 60%, 65%)`;

  const titleLines = wrap(safeTitle, 18, 4);
  const ini = initials(safeTitle, safeAuthor);

  const titleY = h * 0.42;
  const lineHeight = Math.round(h * 0.06);

  const titleSvg = titleLines
    .map(
      (line, i) =>
        `<text x="50%" y="${titleY + i * lineHeight}" text-anchor="middle" font-family="Georgia,serif" font-size="${Math.round(
          h * 0.052
        )}" font-weight="600" fill="#f8fafc">${escapeXml(line)}</text>`
    )
    .join("");

  const authorSvg = safeAuthor
    ? `<text x="50%" y="${h * 0.84}" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,sans-serif" font-size="${Math.round(
        h * 0.034
      )}" fill="#cbd5e1" letter-spacing="1">${escapeXml(safeAuthor.toUpperCase().slice(0, 28))}</text>`
    : "";

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" role="img" aria-label="${escapeXml(
    safeTitle
  )}">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${c1}"/>
      <stop offset="100%" stop-color="${c2}"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" fill="url(#g)"/>
  <rect x="${w * 0.06}" y="${h * 0.06}" width="${w * 0.88}" height="${h * 0.88}" fill="none" stroke="${accent}" stroke-opacity="0.35" stroke-width="1.5"/>
  <circle cx="50%" cy="${h * 0.22}" r="${Math.round(h * 0.075)}" fill="${accent}" fill-opacity="0.18"/>
  <text x="50%" y="${h * 0.235}" text-anchor="middle" font-family="Georgia,serif" font-size="${Math.round(h * 0.075)}" font-weight="700" fill="${accent}">${escapeXml(ini)}</text>
  ${titleSvg}
  ${authorSvg}
  <text x="50%" y="${h * 0.95}" text-anchor="middle" font-family="-apple-system,sans-serif" font-size="${Math.round(h * 0.022)}" fill="#64748b" letter-spacing="2">LEAFNODE</text>
</svg>`;

  // Use encodeURIComponent (works for arbitrary unicode in titles)
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
